'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { CompleteTask } from './complete-task';
import { $morePopover, setPopoverEvent } from './store';

export const POPOVER_WIDTH = 300;

export function MoreItemsPopover({ featureId }: { featureId: string }) {
  const [popover, setPopover] = useUnit([$morePopover, setPopoverEvent]);
  const popoverRef = useRef<HTMLDivElement>(null);

  // зыкрываем поповер при нажатии по врапперу текущего календаря,
  // так нужно чтобы не закрывался поповер например при детальном открытии таска и последующем его закрытии
  const onDocumentMouseDown = useCallback(
    (event: MouseEvent) => {
      if (popover !== null) {
        const target = event.target as Element;
        if (target && target.closest('.fc-more-popover')) {
          return;
        } else if (popover.container.contains(event.target as Node)) {
          setPopover(null);
        }
      }
    },
    [popover, setPopover]
  );

  useEffect(() => {
    if (popover !== null && popover.featureId === featureId) {
      document.addEventListener('mousedown', onDocumentMouseDown, false);
      return () => {
        document.removeEventListener('mousedown', onDocumentMouseDown, false);
      };
    }
  }, [onDocumentMouseDown, popover, featureId]);

  return popover !== null && popover.featureId === featureId ? (
    <div
      className={classNames({
        'flex items-center justify-center fixed top-0 left-0 w-full h-full z-[1000]': !popover.pos,
        'contents': popover.pos !== undefined,
      })}
    >
      <div
        className={classNames('fc-more-popover border rounded-md p-5', {
          'z-[1000] absolute': popover.pos !== undefined,
        })}
        style={{
          width: POPOVER_WIDTH + 'px',
          top: popover.pos ? popover.pos.y + 'px' : 'unset',
          left: popover.pos ? popover.pos.x + 'px' : 'unset',
        }}
        ref={popoverRef}
      >
        <div className="text-sm font-bold mb-3 flex justify-between items-center">
          {popover.date.toLocaleString('en', { month: 'long', day: '2-digit', year: 'numeric' })}
          <span className="cursor-pointer" onClick={() => setPopover(null)}>
            <Icon icon={IconsMap.CLOSE} className="w-4" />
          </span>
        </div>
        <div
          className={classNames({
            'max-h-[65vh] overflow-y-auto': !popover.pos,
            'contents': popover.pos !== undefined,
          })}
        >
          {popover.tasks.map((task) => {
            return (
              <div
                className={classNames(
                  'calendar-event text-xs rounded border mt-1 py-1 px-2 cursor-pointer text-black',
                  { done: task.isDone }
                )}
                key={task.id}
                onClick={(event) => {
                  popover.onTaskClick({ event: { id: task.id as string } });
                }}
              >
                <span className="w-full whitespace-nowrap cursor-pointer flex items-center">
                  <CompleteTask isDone={task.isDone} taskId={task.id as string} completeTask={popover.completeTask} />
                  <TippyTooltip
                    message={
                      <div style={{ maxWidth: '120px', whiteSpace: 'normal', textAlign: 'center' }}>{task.title}</div>
                    }
                  >
                    <span className="fc-event-name block overflow-hidden text-ellipsis flex-1">{task.title}</span>
                  </TippyTooltip>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;
}
